import { useCallback, useEffect, useState } from 'react';
import { autoplannerAPI } from '../services/api';

/**
 * useExcludedRowers
 * Encapsulates loading/saving and manipulating excluded_rowers_per_session for a task.
 */
const useExcludedRowers = (taskId, options = {}) => {
  const { onSaved } = options;

  const [excludedRowersPerSession, setExcludedRowersPerSession] = useState({});
  const [loadingExcludedRowers, setLoadingExcludedRowers] = useState(false);
  const [savingExcludedRowers, setSavingExcludedRowers] = useState(false);

  const loadExcludedRowers = useCallback(async () => {
    try {
      if (!taskId) return;
      setLoadingExcludedRowers(true);
      const result = await autoplannerAPI.getNormalizedDataset(taskId);
      if (result?.success && result.normalized_data?.success) {
        const excludedRowers =
          result.normalized_data.normalized_data?.display_data?.excluded_rowers_per_session || {};
        setExcludedRowersPerSession(excludedRowers);
      }
    } catch (e) {
      console.error('Error loading excluded rowers per session:', e);
    } finally {
      setLoadingExcludedRowers(false);
    }
  }, [taskId]);

  const saveExcludedRowers = useCallback(
    async (nextMap) => {
      try {
        if (!taskId) return;
        setSavingExcludedRowers(true);
        const result = await autoplannerAPI.saveExcludedRowersPerSession(taskId, nextMap);
        if (result?.success) {
          setExcludedRowersPerSession(nextMap || {});
          if (typeof onSaved === 'function') onSaved();
        }
      } catch (e) {
        console.error('Error saving excluded rowers per session:', e);
      } finally {
        setSavingExcludedRowers(false);
      }
    },
    [taskId, onSaved]
  );

  // Convenience mutators working over the local map and delegating to save
  const addExcludedRower = useCallback(
    (sessionId, userId, reason = 'Manually excluded') => {
      setExcludedRowersPerSession((prev) => {
        const next = { ...prev };
        const list = Array.isArray(next[sessionId]) ? [...next[sessionId]] : [];
        if (!list.some((ex) => ex.user_id === userId)) {
          list.push({ user_id: userId, reason });
        }
        next[sessionId] = list;
        // fire-and-forget save; callers may await saveExcludedRowers separately if needed
        saveExcludedRowers(next);
        return next;
      });
    },
    [saveExcludedRowers]
  );

  const removeExcludedRower = useCallback(
    (sessionId, userId) => {
      setExcludedRowersPerSession((prev) => {
        const next = { ...prev };
        const list = (next[sessionId] || []).filter((ex) => ex.user_id !== userId);
        if (list.length === 0) delete next[sessionId];
        else next[sessionId] = list;
        saveExcludedRowers(next);
        return next;
      });
    },
    [saveExcludedRowers]
  );

  const updateExcludedRowerReason = useCallback(
    (sessionId, userId, reason) => {
      setExcludedRowersPerSession((prev) => {
        const next = { ...prev };
        const list = (next[sessionId] || []).map((ex) =>
          ex.user_id === userId ? { ...ex, reason } : ex
        );
        next[sessionId] = list;
        saveExcludedRowers(next);
        return next;
      });
    },
    [saveExcludedRowers]
  );

  const bulkExcludeRower = useCallback(
    (sessionIds, userId, reason = 'Bulk excluded') => {
      setExcludedRowersPerSession((prev) => {
        const next = { ...prev };
        (sessionIds || []).forEach((sid) => {
          const list = Array.isArray(next[sid]) ? [...next[sid]] : [];
          if (!list.some((ex) => ex.user_id === userId)) {
            list.push({ user_id: userId, reason });
          }
          next[sid] = list;
        });
        saveExcludedRowers(next);
        return next;
      });
    },
    [saveExcludedRowers]
  );

  const bulkIncludeRower = useCallback(
    (sessionIds, userId) => {
      setExcludedRowersPerSession((prev) => {
        const next = { ...prev };
        (sessionIds || []).forEach((sid) => {
          const list = (next[sid] || []).filter((ex) => ex.user_id !== userId);
          if (list.length === 0) delete next[sid];
          else next[sid] = list;
        });
        saveExcludedRowers(next);
        return next;
      });
    },
    [saveExcludedRowers]
  );

  useEffect(() => {
    loadExcludedRowers();
  }, [loadExcludedRowers]);

  return {
    excludedRowersPerSession,
    loadingExcludedRowers,
    savingExcludedRowers,
    loadExcludedRowers,
    saveExcludedRowers,
    addExcludedRower,
    removeExcludedRower,
    updateExcludedRowerReason,
    bulkExcludeRower,
    bulkIncludeRower,
  };
};

export default useExcludedRowers;
